import * as Phaser from 'phaser';
import {
  type PaintedSceneAssetSpec,
  type SceneLayer,
  samplePerspectiveScale,
  isDepthBracketSpec,
  resolveLayerBracket,
  computeMaxBracket,
} from './sceneAssetSpec.schema';
import type { AmbientLight } from '@game/shared';

const PLACEHOLDER_TEX_KEY = '__painted-placeholder';

const BRACKET_DEPTH_SPACING = 100;

export interface LayerRuntime {
  spec: SceneLayer;
  image: Phaser.GameObjects.Image | null;
  graphics: Phaser.GameObjects.Graphics | null;
  baseX: number;
  baseY: number;
  baseRotation: number;
  baseAlpha: number;
  /** Resolved depth bracket (populated when spec uses bracket layering). */
  resolvedBracket: number;
}

export class ParallaxLayerManager {
  private readonly scene: Phaser.Scene;
  readonly layers: LayerRuntime[] = [];
  private texturePrefix: string;
  private useBrackets = false;
  private maxBracket = 0;

  constructor(scene: Phaser.Scene, texturePrefix: string) {
    this.scene = scene;
    this.texturePrefix = texturePrefix;
  }

  layerTextureKey(layerId: string): string {
    return `${this.texturePrefix}:${layerId}`;
  }

  preloadLayerImages(spec: PaintedSceneAssetSpec): void {
    for (const layer of spec.layers) {
      const texKey = this.layerTextureKey(layer.id);
      if (layer.src && !this.scene.textures.exists(texKey)) {
        this.scene.load.image(texKey, layer.src);
      }
    }
  }

  buildLayers(spec: PaintedSceneAssetSpec): void {
    this.installPlaceholderTexture();
    const viewW = this.scene.scale.width;
    const worldW = viewW * (spec.worldWidthFactor ?? 1);
    const h = this.scene.scale.height;
    this.layers.length = 0;

    this.useBrackets = isDepthBracketSpec(spec);
    this.maxBracket = this.useBrackets ? computeMaxBracket(spec) : 0;

    for (const layer of spec.layers) {
      const texKey = this.layerTextureKey(layer.id);
      // A layer must cover what the camera can see of it across the whole
      // scroll range. At scroll s it shows world span [s*f, s*f + view], so the
      // widest it ever needs to be is view + (world - view) * f. A foreground
      // plane at f=1.45 therefore needs to be WIDER than the world, and a far
      // plane at f=0.18 can be much narrower. Stretching everything to the
      // world width — as this did — leaves the fast planes running out before
      // the camera does, which shows up as a hard vertical seam.
      const span = Math.max(viewW, viewW + (worldW - viewW) * layer.parallax);
      if (layer.placeholder) {
        this.layers.push(this.buildPlaceholderLayer(layer, span, h));
      } else if (layer.src && this.scene.textures.exists(texKey)) {
        this.layers.push(this.buildImageLayer(layer, texKey, span, h));
      } else {
        this.layers.push(this.buildPlaceholderLayer({
          ...layer,
          placeholder: { color: '#2a2235', label: `${layer.id} (asset missing)` },
        }, span, h));
      }
    }

    if (this.useBrackets) {
      for (const lr of this.layers) {
        lr.resolvedBracket = resolveLayerBracket(lr.spec, this.maxBracket);
      }
    }
  }

  get hasBrackets(): boolean {
    return this.useBrackets;
  }

  /**
   * Re-sort Phaser depths of all layers based on the walker's current bracket.
   * Returns the Phaser depth the walker sprite should use.
   */
  sortByBracket(walkerBracket: number): { walkerDepth: number; shadowDepth: number } {
    const walkerDepth = walkerBracket * BRACKET_DEPTH_SPACING + BRACKET_DEPTH_SPACING - 1;
    const shadowDepth = walkerDepth - 1;

    for (const lr of this.layers) {
      const phaserDepth = lr.resolvedBracket * BRACKET_DEPTH_SPACING + lr.spec.depth;
      if (lr.image) lr.image.setDepth(phaserDepth);
      if (lr.graphics) lr.graphics.setDepth(phaserDepth);
    }

    return { walkerDepth, shadowDepth };
  }

  tickAmbient(timeMs: number): void {
    const t = timeMs / 1000;
    const w = this.scene.scale.width;
    for (const layer of this.layers) {
      if (layer.spec.organicMotion?.length) continue;
      const ambient = layer.spec.ambient;
      if (!ambient) continue;

      if (ambient.kind === 'linear-drift') {
        const dx = ambient.amplitude * (timeMs / 1000) * (ambient.frequencyHz);
        if (layer.image) {
          layer.image.x = layer.baseX + (dx % w);
          const halfW = layer.image.displayWidth / 2;
          if (layer.image.x - halfW > w) {
            layer.image.x -= w + layer.image.displayWidth;
          }
        }
        continue;
      }

      const phase = ambient.phaseRad ?? 0;
      const wave = Math.sin(t * Math.PI * 2 * ambient.frequencyHz + phase);
      switch (ambient.kind) {
        case 'parallax-drift':
        case 'horizontal-sway': {
          const dx = wave * ambient.amplitude;
          if (layer.image) layer.image.x = layer.baseX + dx;
          else if (layer.graphics) layer.graphics.x = dx;
          break;
        }
        case 'rotate-sway': {
          const radians = wave * ambient.amplitude;
          if (layer.image) layer.image.rotation = layer.baseRotation + radians;
          break;
        }
        case 'alpha-flicker': {
          const a = Phaser.Math.Clamp(layer.baseAlpha + wave * ambient.amplitude, 0, 1);
          if (layer.image) layer.image.setAlpha(a);
          else if (layer.graphics) layer.graphics.setAlpha(a);
          break;
        }
      }
    }
  }

  samplePerspectiveScale(ny: number, spec: PaintedSceneAssetSpec): number {
    return samplePerspectiveScale(ny, spec.perspective);
  }

  addExternalLayer(spec: SceneLayer, image: Phaser.GameObjects.Image | null, graphics?: Phaser.GameObjects.Graphics | null): LayerRuntime {
    const runtime: LayerRuntime = {
      spec,
      image,
      graphics: graphics ?? null,
      baseX: image?.x ?? 0,
      baseY: image?.y ?? 0,
      baseRotation: image?.rotation ?? 0,
      baseAlpha: spec.alpha,
      resolvedBracket: resolveLayerBracket(spec, this.maxBracket),
    };
    this.layers.push(runtime);
    return runtime;
  }

  getLayer(id: string): LayerRuntime | undefined {
    return this.layers.find(l => l.spec.id === id);
  }

  /**
   * How far the ambient colour is allowed to pull a layer away from white.
   *
   * `setTint` MULTIPLIES the texture, so feeding it a saturated ambient colour
   * is destructive rather than atmospheric: dusk's rgb(255,140,80) strips 45%
   * of the green and 69% of the blue out of every pixel, which turned the
   * watercolour greens and the blue distance to orange mud in every painted
   * scene. We keep the hue *direction* of the ambient light but blend most of
   * the way back to white, so the tint shades the painting instead of
   * repainting it. Luminance is the colour grader's job (its brightness term),
   * not the tint's.
   */
  private static readonly TINT_STRENGTH = 0.35;

  /**
   * Layers stay near-opaque. Fading them toward the camera background was a
   * third darkening pass stacked on the dark tint and the grader's brightness
   * curve, which took night to near-black and washed every layer toward the
   * clear colour.
   */
  private static readonly MIN_ALPHA_SCALE = 0.82;

  applyTimeOfDayTint(ambient: AmbientLight): void {
    const s = ParallaxLayerManager.TINT_STRENGTH;
    const blend = (channel: number): number =>
      Math.round(255 + (channel - 255) * s);

    const tint =
      (Phaser.Math.Clamp(blend(ambient.r), 0, 255) << 16) |
      (Phaser.Math.Clamp(blend(ambient.g), 0, 255) << 8) |
      Phaser.Math.Clamp(blend(ambient.b), 0, 255);

    const intensity = Phaser.Math.Clamp(ambient.intensity, 0, 1);
    const alphaScale =
      ParallaxLayerManager.MIN_ALPHA_SCALE +
      (1 - ParallaxLayerManager.MIN_ALPHA_SCALE) * intensity;

    for (const layer of this.layers) {
      if (!layer.image) continue;
      layer.image.setTint(tint);
      layer.image.setAlpha(layer.baseAlpha * alphaScale);
    }
  }

  clearTimeOfDayTint(): void {
    for (const layer of this.layers) {
      if (!layer.image) continue;
      layer.image.clearTint();
      layer.image.setAlpha(layer.baseAlpha);
    }
  }

  scaleForZoom(zoom: number): void {
    if (zoom >= 1 || zoom <= 0) return;
    const compensation = 1 / zoom;
    for (const layer of this.layers) {
      if (layer.image) {
        layer.image.setScale(layer.image.scaleX * compensation, layer.image.scaleY * compensation);
      }
      if (layer.graphics) {
        layer.graphics.setScale(compensation);
      }
    }
  }

  destroy(): void {
    for (const layer of this.layers) {
      if (layer.image) layer.image.destroy();
      if (layer.graphics) layer.graphics.destroy();
    }
    this.layers.length = 0;
  }

  private installPlaceholderTexture(): void {
    if (this.scene.textures.exists(PLACEHOLDER_TEX_KEY)) return;
    const g = this.scene.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0xffffff, 1);
    g.fillRect(0, 0, 4, 4);
    g.generateTexture(PLACEHOLDER_TEX_KEY, 4, 4);
    g.destroy();
  }

  private buildImageLayer(layer: SceneLayer, textureKey: string, w: number, h: number): LayerRuntime {
    const img = this.scene.add.image(w / 2, h / 2, textureKey);
    // Layers pivot about their CENTRE, which matters for any `rotate-sway`:
    // foliage really pivots where it attaches, so a centre pivot shears the
    // plate — top and bottom swing in antiphase. At the amplitudes in use
    // (fg-canopy, 0.0048 rad) worst-case shear across the visible frame is
    // ~3px and reads as texture. It does NOT scale. Doubling a rotate-sway
    // amplitude for "more life" makes the shear visible, and the fix then is
    // to move the pivot, not to accept it.
    img.setOrigin(0.5, 0.5);
    img.setDepth(layer.depth);
    const scaleX = w / img.width;
    const scaleY = h / img.height;
    img.setScale(Math.max(scaleX, scaleY));
    img.setAlpha(layer.alpha);
    // Applies above 1 as well as below: a foreground plane that travels FASTER
    // than the world is what reads as being close to the camera. The old
    // `< 1` guard silently pinned every foreground layer to scroll factor 1.
    img.setScrollFactor(layer.parallax);
    return {
      spec: layer,
      image: img,
      graphics: null,
      baseX: img.x,
      baseY: img.y,
      baseRotation: 0,
      baseAlpha: layer.alpha,
      resolvedBracket: 0,
    };
  }

  private buildPlaceholderLayer(layer: SceneLayer, w: number, h: number): LayerRuntime {
    const placeholder = layer.placeholder ?? { color: '#2a2235', label: layer.id };
    const colour = Phaser.Display.Color.HexStringToColor(placeholder.color).color;
    const g = this.scene.add.graphics();
    g.fillStyle(colour, 1);
    g.fillRect(0, 0, w, h);
    g.setDepth(layer.depth);
    g.setAlpha(layer.alpha);

    if (placeholder.label) {
      const label = this.scene.add.text(8, 8 + layer.depth * 14, placeholder.label, {
        fontFamily: "'Almendra', serif",
        fontSize: '12px',
        color: '#eae0c8',
      });
      label.setDepth(layer.depth);
      label.setAlpha(0.6);
    }

    return {
      spec: layer,
      image: null,
      graphics: g,
      baseX: 0,
      baseY: 0,
      baseRotation: 0,
      baseAlpha: layer.alpha,
      resolvedBracket: 0,
    };
  }
}
